import React, { useState, useEffect } from "react";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { Link, useNavigate } from "react-router-dom";
import Select from 'react-select'; 
import { 
  Card, 
  Table, 
  Row, 
  Col, 
  Form, 
  InputGroup, 
  Button, 
  Pagination, 
  Badge, 
  Modal, 
  Toast,
  ToastContainer,
  Spinner,
  ProgressBar
} from "react-bootstrap";
import axios from "axios";
import {
  Search,
  PlusCircle,
  Eye,
  Pencil,
  Trash,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  ExclamationCircle,
  Layers,
  Activity,
  Tag,
  Briefcase,
  Folder,
  FolderPlus,
  InfoCircle,
  Calendar,
  GraphUp,
  FileText,
  Cloud,
  BarChartFill,
  Globe
} from "react-bootstrap-icons";
import { API_BASE_URL } from "../../constants/api_constants";

// Configuration de l'API

const ACTIVITIES_ENDPOINT = `${API_BASE_URL}/activites`;
const PARENT_CATEGORIES_ENDPOINT = `${API_BASE_URL}/usager-categories`;

export default function ActivitiesList() {
  const navigate = useNavigate();

  // États principaux
  const [activities, setActivities] = useState([]);
  const [parentCategories, setParentCategories] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [error, setError] = useState(null);
  
  // États pour les modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // États pour les données des modals
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [editedActivity, setEditedActivity] = useState(null);
  const [activityToDelete, setActivityToDelete] = useState(null);
  const [newActivity, setNewActivity] = useState({
    name: "",
    description: "",
    code_category: ""
  });
  
  // États pour les chargements
  const [loadingAdd, setLoadingAdd] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  
  // États pour les notifications
  const [alertMessage, setAlertMessage] = useState({ show: false, message: '', type: 'info' });

  // Obtenir le token d'authentification
  const getAuthToken = () => sessionStorage.getItem("authToken");

  // Headers pour les requêtes API
  const getHeaders = () => {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  // Charger les catégories parentes
  const fetchParentCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await axios.get(PARENT_CATEGORIES_ENDPOINT, {
        headers: getHeaders(),
      });
      
      if (response.data && Array.isArray(response.data.data)) {
        setParentCategories(response.data.data);
      } else if (response.data && Array.isArray(response.data)) {
        setParentCategories(response.data);
      } else {
        setParentCategories([]);
      }
    } catch (error) {
      console.error("Erreur chargement catégories parentes:", error);
      setParentCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Charger les activités depuis l'API
  const fetchActivities = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${ACTIVITIES_ENDPOINT}`, {
        headers: getHeaders(),
      });
      
      if (response.data && Array.isArray(response.data)) {
        setActivities(response.data);
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setActivities(response.data.data);
      } else {
        setActivities([]);
      }
    } catch (error) {
      setError(`${error.response?.data?.message || error.message}`);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  // Initialiser les données
  useEffect(() => {
    fetchActivities();
    fetchParentCategories();
  }, []);

  // Fonctions pour les notifications
  const showAlert = (message, type = 'info', duration = 4000) => {
    setAlertMessage({ show: true, message, type });
    if (duration > 0) {
      setTimeout(() => {
        setAlertMessage(prev => ({ ...prev, show: false }));
      }, duration);
    }
  };

  const hideAlert = () => {
    setAlertMessage(prev => ({ ...prev, show: false }));
  };

  const handleRetry = () => {
    setError(null);
    fetchActivities();
  };

  // Obtenir le nom de la catégorie parente par son ID
  const getParentCategoryName = (categoryId) => {
    const category = parentCategories.find(c => c.code_category === categoryId || c.id === categoryId);
    return category ? category.category_name || category.name : "Non catégorisé";
  };

  const parentCategoryOptions = React.useMemo(() => {
    return parentCategories.map(cat => ({
      value: cat.code_category || cat.id,
      label: cat.category_name || cat.name
    }));
  }, [parentCategories]);

  const selectedParentCategory = React.useMemo(() => {
    if (!newActivity.code_category) return null;
    return parentCategoryOptions.find(opt => opt.value === newActivity.code_category);
  }, [newActivity.code_category, parentCategoryOptions]);

  // Filtrage et tri des données
  const filteredAndSortedItems = React.useMemo(() => {
    let filtered = [...activities];
    
    if (filterText) {
      const searchTerm = filterText.toLowerCase();
      filtered = filtered.filter(item => 
        (item.name && item.name.toLowerCase().includes(searchTerm)) ||
        (item.description && item.description.toLowerCase().includes(searchTerm)) ||
        (item.code_category && item.code_category.toString().toLowerCase().includes(searchTerm))
      );
    }

    filtered.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      if (sortField === 'name') {
        aValue = a.name || '';
        bValue = b.name || '';
      } else if (sortField === 'code_category') {
        aValue = a.code_category || '';
        bValue = b.code_category || '';
      } else if (sortField === 'created_at') {
        aValue = a.created_at || '';
        bValue = b.created_at || '';
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [activities, filterText, sortField, sortDirection]);

  // Statistiques
  const stats = React.useMemo(() => {
    const total = activities.length;
    const withDescription = activities.filter(c => c.description && c.description.trim() !== '').length;
    const descriptionRate = total > 0 ? Math.round((withDescription / total) * 100) : 0;
    const categoriesCount = new Set(activities.map(a => a.code_category)).size;
    
    return { 
      total, 
      withDescription,
      descriptionRate,
      categoriesCount
    };
  }, [activities]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  // Fonctions de tri
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Gestion des sélections
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedActivities(currentItems.map(activity => activity.id));
    } else {
      setSelectedActivities([]);
    }
  };

  const handleSelectActivity = (activityId) => {
    setSelectedActivities(prev => 
      prev.includes(activityId) 
        ? prev.filter(id => id !== activityId)
        : [...prev, activityId]
    );
  };

  const isAllSelected = currentItems.length > 0 && selectedActivities.length === currentItems.length;
  const isSomeSelected = selectedActivities.length > 0 && selectedActivities.length < currentItems.length;

  // Fonctions de formatage
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obtenir la couleur du badge
  const getActivityColor = (id) => {
    const colors = ["primary", "success", "info", "warning", "dark", "secondary"];
    return colors[Math.abs(id) % colors.length];
  };

  // Gestion de la suppression
  const handleDeleteActivity = (activity) => {
    setActivityToDelete(activity);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!activityToDelete) return;

    try {
      setLoadingDelete(true);
      
      await axios.delete(`${ACTIVITIES_ENDPOINT}/${activityToDelete.id_export || activityToDelete.id}`, {
        headers: getHeaders(),
      });

      setActivities(prev => prev.filter(a => a.id !== activityToDelete.id));
      setSelectedActivities(prev => prev.filter(id => id !== activityToDelete.id));
      showAlert('Activité supprimée avec succès', 'success');
      fetchActivities();
    } catch (error) {
      console.error("Erreur suppression:", error);
      showAlert(`Erreur lors de la suppression: ${error.message}`, 'danger');
    } finally {
      setLoadingDelete(false);
      setShowDeleteModal(false);
      setActivityToDelete(null);
    }
  };

  // Gestion de la modification
  const handleEditActivity = (activity) => {
    setSelectedActivity(activity);
    setEditedActivity({
      id: activity.id,
      id_export: activity.id_export,
      name: activity.name || "",
      description: activity.description || "",
      code_category: activity.code_category || ""
    });
    setShowEditModal(true);
  };

  // Gestion de l'ajout
  const handleAddActivity = async () => {
    if (!newActivity.name.trim()) {
      showAlert('Le nom de l\'activité est requis', 'warning');
      return;
    }

    if (!newActivity.code_category) {
      showAlert('Veuillez sélectionner une catégorie parente', 'warning');
      return;
    }

    try {
      setLoadingAdd(true);
      
      const payload = {
        name: newActivity.name,
        description: newActivity.description || "",
        code_category: Number(newActivity.code_category),
        date_activite: new Date().toISOString().split('T')[0]
      };
      
      const response = await axios.post(
        ACTIVITIES_ENDPOINT,
        payload,
        { headers: getHeaders() }
      );
      
      setActivities(prev => [...prev, response.data]);
      
      setShowAddModal(false);
      setNewActivity({
        name: "",
        description: "",
        code_category: ""
      });

      showAlert('Activité ajoutée avec succès', 'success');
      await fetchActivities();
      
    } catch (error) {
      console.error("Erreur ajout:", error);
      showAlert(error.response?.data?.message || "Erreur lors de l'ajout", 'danger');
    } finally {
      setLoadingAdd(false);
    }
  };

  // Gestion de la modification
  const handleSaveEdit = async () => {
    if (!editedActivity) return;

    if (!editedActivity.name?.trim()) {
      showAlert('Le nom de l\'activité est requis', 'warning');
      return;
    }

    if (!editedActivity.code_category) {
      showAlert('Veuillez sélectionner une catégorie parente', 'warning');
      return;
    }

    try {
      setLoadingUpdate(true);
      
      const payload = {
        name: editedActivity.name,
        description: editedActivity.description,
        code_category: Number(editedActivity.code_category)
      };
      
      await axios.put(
        `${ACTIVITIES_ENDPOINT}/${editedActivity.id_export}`,
        payload,
        { headers: getHeaders() }
      );

      setActivities(prev => prev.map(a => 
        a.id === editedActivity.id ? { ...a, ...payload } : a
      ));
      
      setShowEditModal(false);
      showAlert('Activité modifiée avec succès', 'success');
      fetchActivities();
    } catch (error) {
      console.error("Erreur modification:", error);
      showAlert(`Erreur lors de la modification: ${error.response?.data?.message || error.message}`, 'danger');
    } finally {
      setLoadingUpdate(false);
    }
  };

  // Voir les détails
  const handleViewDetails = (activity) => {
    setSelectedActivity(activity);
    setShowDetailsModal(true);
  };

  // Composant pour l'en-tête triable
  const SortableHeader = ({ field, children }) => (
    <th 
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => handleSort(field)}
      className="position-relative"
    >
      {children}
      {sortField === field && (
        <span className="ms-1">
          {sortDirection === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </span>
      )}
    </th>
  );

  const handleRefresh = () => {
    fetchActivities();
    fetchParentCategories();
  };

  return (
    <React.Fragment>
      <Header />
      
      <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
        <Toast show={alertMessage.show} onClose={hideAlert} bg={alertMessage.type} delay={4000} autohide>
          <Toast.Header closeButton={false}>
            {alertMessage.type === 'success' && <CheckCircle className="me-2 text-success" />}
            {alertMessage.type === 'danger' && <XCircle className="me-2 text-danger" />}
            {alertMessage.type === 'warning' && <ExclamationCircle className="me-2 text-warning" />}
            {alertMessage.type === 'info' && <CheckCircle className="me-2 text-info" />}
            <strong className="me-auto">Notification</strong>
            <small>maintenant</small>
          </Toast.Header>
          <Toast.Body className="text-white fw-medium">{alertMessage.message}</Toast.Body>
        </Toast>
      </ToastContainer>
      
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f0f2f5', minHeight: '100vh' }}>
        <div className="container-fluid">
          {/* En-tête */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                <div className="mb-3 mb-md-0">
                  <ol className="breadcrumb fs-sm mb-1">
                    <li className="breadcrumb-item"><Link to="/admin/dashboard">Tableau de bord</Link></li>
                    <li className="breadcrumb-item active" aria-current="page">Activités</li>
                  </ol>
                  <h4 className="main-title mb-0 d-flex align-items-center">
                    <Briefcase className="me-2 text-primary" size={28} />
                    Gestion des Activités
                  </h4>
                  <p className="text-muted mb-0">Consultez et gérez toutes les activités des usagers</p>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  {/* {selectedActivities.length > 0 && (
                    <Button variant="outline-danger" size="sm" onClick={() => {
                      const toDelete = activities.find(c => selectedActivities.includes(c.id));
                      if (toDelete) {
                        setActivityToDelete(toDelete);
                        setShowDeleteModal(true);
                      }
                    }} className="d-flex align-items-center">
                      <Trash className="me-2" size={16} />Supprimer ({selectedActivities.length})
                    </Button>
                  )} */}
                  
                  <Button variant="success" onClick={() => navigate('/contribuables/activity/new')} className="d-flex align-items-center shadow-sm">
                    <PlusCircle className="me-2" size={16} />Nouvelle Activité
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Cartes de statistiques */}
          {/* <Row className="g-3 mb-4">
            <Col md={3}>
              <Card className="border-0 shadow-sm rounded-3 h-100 stats-card">
                <div className="card-gradient bg-primary"></div>
                <Card.Body className="d-flex align-items-center">
                  <div className="bg-primary bg-opacity-10 p-3 rounded-3 me-3">
                    <Briefcase size={28} className="text-primary" />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Total Activités</h6>
                    <h3 className="mb-0 fw-bold">{stats.total}</h3>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm rounded-3 h-100 stats-card">
                <div className="card-gradient bg-success"></div>
                <Card.Body className="d-flex align-items-center">
                  <div className="bg-success bg-opacity-10 p-3 rounded-3 me-3">
                    <FileText size={28} className="text-success" />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Avec Description</h6>
                    <h3 className="mb-0 fw-bold">{stats.withDescription}</h3>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm rounded-3 h-100 stats-card">
                <div className="card-gradient bg-info"></div>
                <Card.Body className="d-flex align-items-center">
                  <div className="bg-info bg-opacity-10 p-3 rounded-3 me-3">
                    <BarChartFill size={28} className="text-info" />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Taux de description</h6>
                    <h3 className="mb-0 fw-bold">{stats.descriptionRate}%</h3>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm rounded-3 h-100 stats-card">
                <div className="card-gradient bg-warning"></div>
                <Card.Body className="d-flex align-items-center">
                  <div className="bg-warning bg-opacity-10 p-3 rounded-3 me-3">
                    <Layers size={28} className="text-warning" />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Catégories</h6>
                    <h3 className="mb-0 fw-bold">{stats.categoriesCount}</h3>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row> */}

          {/* Barre de recherche */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="border-0 shadow-sm rounded-3">
                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <InputGroup>
                        <InputGroup.Text className="bg-light border-end-0">
                          <Search className="text-primary" />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Rechercher par nom, description, catégorie..."
                          value={filterText}
                          onChange={(e) => setFilterText(e.target.value)}
                          className="border-start-0 bg-light"
                        />
                        {filterText && (
                          <Button variant="outline-secondary" className="border-start-0" onClick={() => setFilterText('')}>
                            <X size={16} />
                          </Button>
                        )}
                      </InputGroup>
                    </Col>
                    <Col md={3}>
                      <Form.Select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="bg-light"
                      >
                        <option value="5">5 / page</option>
                        <option value="10">10 / page</option>
                        <option value="25">25 / page</option>
                        <option value="50">50 / page</option>
                      </Form.Select>
                    </Col>
                    <Col md={3}>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-secondary"
                          onClick={() => {
                            setFilterText("");
                            setCurrentPage(1);
                            setSelectedActivities([]);
                          }}
                          className="flex-grow-1 d-flex align-items-center justify-content-center"
                        >
                          <X className="me-2" size={16} />Réinitialiser
                        </Button>
                        <Button
                          variant="outline-primary"
                          onClick={handleRefresh}
                          className="d-flex align-items-center justify-content-center"
                          disabled={loading}
                        >
                          <Cloud size={16} />
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Tableau principal */}
          <Row>
            <Col md={12}>
              <Card className="shadow-sm border-0 rounded-3">
                <Card.Header className="bg-primary d-flex justify-content-between align-items-center py-3 border-bottom">
                  <div className="d-flex align-items-center">
                    <Briefcase className="me-2 text-white" />
                    <span className="fw-semibold text-white">Liste des Activités ({filteredAndSortedItems.length})</span>
                    {filterText && <Badge bg="info" className="ms-2 rounded-pill">Filtre actif</Badge>}
                  </div>
                  <div className="text-white small d-flex align-items-center">
                    {selectedActivities.length > 0 && (
                      <span className="me-3">{selectedActivities.length} sélectionné(s)</span>
                    )}
                  </div>
                </Card.Header>
                
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="mb-0 align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ width: '40px' }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              style={{ borderRadius: '50%', width: '18px', height: '18px', cursor: 'pointer' }}
                              checked={isAllSelected}
                              ref={input => { if (input) input.indeterminate = isSomeSelected; }}
                              onChange={handleSelectAll}
                            />
                          </th>
                          <th>ID</th>
                          <SortableHeader field="code_category">Catégorie</SortableHeader>
                          <SortableHeader field="name">Nom de l'activité</SortableHeader>
                          <th>Description</th>
                          <SortableHeader field="created_at">Créé le</SortableHeader>
                          <th style={{ width: '130px' }} className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading && activities.length === 0 ? (
                          <tr><td colSpan="7" className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2 text-muted">Chargement...</p>
                          </td></tr>
                        ) : error && activities.length === 0 ? (
                          <tr><td colSpan="7" className="text-center py-5">
                            <ExclamationCircle size={48} className="text-danger mb-3" />
                            <h5>Erreur de chargement</h5>
                            <p>{error}</p>
                            <Button variant="primary" onClick={handleRefresh}>Réessayer</Button>
                          </td></tr>
                        ) : currentItems.length > 0 ? (
                          currentItems.map((activity) => {
                            const color = getActivityColor(activity.id);
                            return (
                              <tr key={activity.id}>
                                <td>
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    style={{ borderRadius: '50%', width: '18px', height: '18px', cursor: 'pointer' }}
                                    checked={selectedActivities.includes(activity.id)}
                                    onChange={() => handleSelectActivity(activity.id)}
                                  />
                                </td>
                                <td><Badge bg="light" text="dark" className="fw-semibold">{activity.id}</Badge></td>
                                <td>
                                  <Badge bg={color} className="px-3 py-2 rounded-pill">
                                    {getParentCategoryName(activity.code_category)}
                                  </Badge>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <Briefcase size={16} className="text-primary me-2" />
                                    <strong className="d-block">{activity.name}</strong>
                                  </div>
                                </td>
                                <td>
                                  <span className="text-muted small">
                                    {activity.description 
                                      ? (activity.description.length > 50 
                                        ? activity.description.substring(0, 50) + '...' 
                                        : activity.description)
                                      : "Aucune description"}
                                  </span>
                                </td>
                                <td><small className="text-muted">{formatDate(activity.created_at)}</small></td>
                                <td>
                                  <div className="d-flex gap-1 justify-content-center">
                                    <Button 
  size="sm" 
  variant="outline-primary" 
  title="Voir détails" 
  onClick={() => navigate(`/agent/contribuable/activities/${activity.id_export}`)}
  className="rounded-circle"
  style={{ width: '34px', height: '34px', padding: 0 }}
>
  <Eye size={14} />
</Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline-info" 
                                      title="Modifier" 
                                      onClick={() => handleEditActivity(activity)}
                                      className="rounded-circle"
                                      style={{ width: '34px', height: '34px', padding: 0 }}
                                    >
                                      <Pencil size={14} />
                                    </Button>
                                    {/* <Button 
                                      size="sm" 
                                      variant="outline-danger" 
                                      title="Supprimer" 
                                      onClick={() => handleDeleteActivity(activity)}
                                      className="rounded-circle"
                                      style={{ width: '34px', height: '34px', padding: 0 }}
                                    >
                                      <Trash size={14} />
                                    </Button> */}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center py-5">
                              <Briefcase size={48} className="text-muted opacity-25" />
                              <h5 className="mt-3 text-muted">AUCUNE ACTIVITÉ TROUVÉE</h5>
                              <p className="text-muted small">Aucune activité ne correspond à vos critères</p>
                              {filterText && (
                                <Button variant="outline-primary" size="sm" onClick={() => setFilterText('')}>
                                  Réinitialiser la recherche
                                </Button>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {totalPages > 1 && (
                    <div className="p-3 border-top d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3">
                      <div className="text-muted small">
                        Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> sur <strong>{filteredAndSortedItems.length}</strong> activités
                        {selectedActivities.length > 0 && (
                          <span className="ms-2 text-primary">• {selectedActivities.length} sélectionné(s)</span>
                        )}
                      </div>
                      <Pagination className="mb-0">
                        <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} />
                        {[...Array(Math.min(totalPages, 7))].map((_, index) => {
                          const pageNum = index + 1;
                          return (
                            <Pagination.Item key={pageNum} active={pageNum === currentPage} onClick={() => setCurrentPage(pageNum)} className={pageNum === currentPage ? 'fw-bold' : ''}>
                              {pageNum}
                            </Pagination.Item>
                          );
                        })}
                        {totalPages > 7 && (
                          <>
                            <Pagination.Ellipsis />
                            <Pagination.Item onClick={() => setCurrentPage(totalPages)}>{totalPages}</Pagination.Item>
                          </>
                        )}
                        <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} />
                      </Pagination>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        {/* MODAL D'AJOUT */}
        <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered size="lg">
          <Modal.Header closeButton className="text-white" style={{ background: "linear-gradient(135deg, #198754, #157347)" }}>
            <Modal.Title className="d-flex align-items-center">
              <PlusCircle className="me-2" />Nouvelle Activité
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Form>
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Form.Label className="fw-semibold mb-0">
                        <Folder className="me-1" size={16} />Catégorie <span className="text-danger">*</span>
                      </Form.Label>
                      {parentCategories.length === 0 && !loadingCategories && (
                        <Button variant="link" size="sm" className="text-primary p-0" onClick={() => {
                          setShowAddModal(false);
                          navigate('/agent/contribuables/Categories');
                        }}>
                          <FolderPlus className="me-1" size={14} />Créer une catégorie
                        </Button>
                      )}
                    </div>
                    
                    {parentCategories.length === 0 && !loadingCategories ? (
                      <div className="text-center p-4 border rounded-3 bg-light">
                        <p className="mb-2">Aucune catégorie disponible</p>
                        <Button variant="primary" size="sm" onClick={() => {
                          setShowAddModal(false);
                          navigate('/agent/contribuables/Categories');
                        }}>
                          <PlusCircle className="me-2" size={14} />Créer une catégorie
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Select
                          options={parentCategoryOptions}
                          value={selectedParentCategory}
                          onChange={(option) => setNewActivity({ ...newActivity, code_category: option?.value || "" })}
                          isLoading={loadingCategories}
                          isClearable isSearchable
                          placeholder="Rechercher une catégorie..."
                          noOptionsMessage={() => "Aucune catégorie trouvée"}
                          loadingMessage={() => "Chargement des catégories..."}
                          styles={{
                            control: (base) => ({
                              ...base,
                              borderRadius: '8px',
                              borderColor: '#e0e0e0',
                              boxShadow: 'none',
                              '&:hover': { borderColor: '#0d6efd' }
                            }),
                            option: (base, state) => ({
                              ...base,
                              backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e8f0fe' : 'white',
                              color: state.isSelected ? 'white' : '#333',
                              padding: '10px 15px',
                              cursor: 'pointer'
                            })
                          }}
                        />
                        <Form.Text className="text-muted">Catégorie pour l'organisation des activités</Form.Text>
                      </>
                    )}
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <Briefcase className="me-1" size={16} />Nom de l'activité <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control type="text" placeholder="Entrez le nom de l'activité" value={newActivity.name} onChange={(e) => setNewActivity({ ...newActivity, name: e.target.value })} className="py-2" required />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold"><FileText className="me-1" size={16} />Description</Form.Label>
                    <Form.Control as="textarea" rows={3} placeholder="Description détaillée de l'activité..." value={newActivity.description} onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })} className="py-2" />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer className="px-4 py-3">
            <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={loadingAdd}>
              <X className="me-2" size={16} />Annuler
            </Button>
            <Button variant="success" onClick={handleAddActivity} disabled={loadingAdd || !newActivity.name.trim() || !newActivity.code_category || parentCategories.length === 0}>
              {loadingAdd ? <><Spinner size="sm" className="me-2" />Enregistrement...</> : <><Check className="me-2" size={16} />Enregistrer l'activité</>}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* MODAL D'ÉDITION */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg">
          <Modal.Header closeButton className="text-white" style={{ background: "linear-gradient(135deg, #0dcaf0, #0b5ed7)" }}>
            <Modal.Title className="d-flex align-items-center"><Pencil size={20} className="me-2" />Modifier l'activité</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {editedActivity && (
              <Form>
                <Row className="g-3">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold"><Folder className="me-1" size={16} />Catégorie <span className="text-danger">*</span></Form.Label>
                      <Form.Select value={editedActivity.code_category || ''} onChange={(e) => setEditedActivity({ ...editedActivity, code_category: e.target.value })} className="py-2">
                        <option value="">-- Sélectionner une catégorie --</option>
                        {parentCategories.map(cat => (
                          <option key={cat.code_category || cat.id} value={cat.code_category || cat.id}>
                            {cat.category_name || cat.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold"><Briefcase className="me-1" size={16} />Nom de l'activité <span className="text-danger">*</span></Form.Label>
                      <Form.Control type="text" placeholder="Entrez le nom de l'activité" value={editedActivity.name || ''} onChange={(e) => setEditedActivity({ ...editedActivity, name: e.target.value })} className="py-2" required />
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold"><FileText className="me-1" size={16} />Description</Form.Label>
                      <Form.Control as="textarea" rows={3} placeholder="Description détaillée de l'activité..." value={editedActivity.description || ''} onChange={(e) => setEditedActivity({ ...editedActivity, description: e.target.value })} className="py-2" />
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer className="px-4 py-3">
            <Button variant="outline-secondary" onClick={() => setShowEditModal(false)} disabled={loadingUpdate}>
              <X className="me-2" size={16} />Annuler
            </Button>
            <Button variant="info" className="text-white" onClick={handleSaveEdit} disabled={loadingUpdate || !(editedActivity?.name)?.trim() || !editedActivity?.code_category}>
              {loadingUpdate ? <><Spinner size="sm" className="me-2" />Mise à jour...</> : <><Check size={18} className="me-2" />Mettre à jour</>}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* MODAL DE DÉTAILS */}
        <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} centered size="lg">
          <Modal.Header closeButton className="border-0 text-white" style={{ background: "linear-gradient(135deg, #0d6efd, #0b5ed7)" }}>
            <Modal.Title className="d-flex align-items-center"><Eye className="me-2" size={20} />Détails de l'activité</Modal.Title>
          </Modal.Header>
          <Modal.Body className="bg-light p-4">
            {selectedActivity && (
              <>
                <div className="mb-4 text-center">
                  <div className="bg-white rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3 shadow-sm" style={{ width: 80, height: 80 }}>
                    <Briefcase size={40} className="text-primary" />
                  </div>
                  <h4 className="fw-bold mb-1">{selectedActivity.name}</h4>
                  <div className="d-flex justify-content-center gap-2 mt-2">
                    <Badge bg="light" text="dark" className="px-3 py-2">ID: {selectedActivity.id}</Badge>
                  </div>
                </div>
                <Row className="g-3">
                  <Col md={12}>
                    <Card className="border-0 shadow-sm">
                      <Card.Body>
                        <div className="d-flex align-items-center mb-2">
                          <Folder className="text-primary me-2" size={18} />
                          <span className="fw-semibold">Catégorie</span>
                        </div>
                        <Badge bg="primary" className="px-3 py-2 rounded-pill">
                          {getParentCategoryName(selectedActivity.code_category)}
                        </Badge>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={12}>
                    <Card className="border-0 shadow-sm">
                      <Card.Body>
                        <div className="d-flex align-items-center mb-2">
                          <FileText className="text-primary me-2" size={18} />
                          <span className="fw-semibold">Description</span>
                        </div>
                        <p className="text-muted mb-0">{selectedActivity.description || "Aucune description fournie"}</p>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={6}>
                    <Card className="border-0 shadow-sm">
                      <Card.Body>
                        <div className="d-flex align-items-center mb-2">
                          <Calendar className="text-success me-2" size={18} />
                          <span className="fw-semibold">Date de création</span>
                        </div>
                        <span>{formatDate(selectedActivity.created_at)}</span>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={6}>
                    <Card className="border-0 shadow-sm">
                      <Card.Body>
                        <div className="d-flex align-items-center mb-2">
                          <InfoCircle className="text-warning me-2" size={18} />
                          <span className="fw-semibold">ID Export</span>
                        </div>
                        <code>{selectedActivity.id_export || "-"}</code>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>Fermer</Button>
            <Button variant="primary" onClick={() => {
              setShowDetailsModal(false);
              handleEditActivity(selectedActivity);
            }}>
              <Pencil className="me-2" size={14} />Modifier
            </Button>
          </Modal.Footer>
        </Modal>

        {/* MODAL DE SUPPRESSION */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title><ExclamationCircle className="text-warning me-2" />Confirmer la suppression</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center py-4">
            <Trash className="display-4 text-danger mb-3" size={48} />
            <h5>Êtes-vous sûr de vouloir supprimer cette activité ?</h5>
            {activityToDelete && (
              <div className="mt-3 p-3 bg-light rounded">
                <strong>{activityToDelete.name}</strong>
                <br />
                <small className="text-muted">ID: {activityToDelete.id}</small>
              </div>
            )}
            <p className="text-muted mt-3">Cette action est irréversible.</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={loadingDelete}>
              <X className="me-2" />Annuler
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete} disabled={loadingDelete}>
              {loadingDelete ? <><Spinner size="sm" className="me-2" />Suppression...</> : <><Trash className="me-2" />Supprimer</>}
            </Button>
          </Modal.Footer>
        </Modal>

        <style>{`
          .stats-card { transition: transform 0.3s ease, box-shadow 0.3s ease; border-radius: 12px; overflow: hidden; }
          .stats-card:hover { transform: translateY(-5px); box-shadow: 0 10px 30px rgba(0,0,0,0.1) !important; }
          .card-gradient { position: absolute; top: 0; left: 0; width: 100%; height: 4px; opacity: 0.8; }
          .rounded-circle { border-radius: 50% !important; }
          .form-control:focus, .form-select:focus { border-color: #0d6efd; box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25); }
        `}</style>
        <Footer />
      </div>
    </React.Fragment>
  );
}